import { useEffect } from 'react';
import { ArrowLeft, Printer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/ui/Button';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useLocation } from '@/shared/hooks/useLocation';
import { formatDate } from '@/shared/utils/format';
import { useQuery } from '@tanstack/react-query';
import { locationsApi } from '@/modules/settings/api/locationsApi';
import { settingsKeys } from '@/modules/settings/queryKeys';
import { safetyApi } from '../api/safetyApi';
import { safetyKeys } from '../queryKeys';
import { useSafetyLocationId, useSafetyRecords } from '../hooks';
import { PnTag } from '../components/SafetyBits';
import styles from './SafetyBinderPage.module.css';

/**
 * Inspection binder — a print-ready document (browser print → PDF). Rendered
 * outside the app chrome so the printout contains only the binder. Assembled
 * entirely from recorded data; no LLM is involved (PRD §13).
 */
export function SafetyBinderPage() {
  const navigate = useNavigate();
  const { currentLocation, setCurrentLocation, setLocations } = useLocation();
  const locationId = useSafetyLocationId();
  const { data: records } = useSafetyRecords();
  const { data: logs } = useQuery({
    queryKey: safetyKeys.logs(locationId),
    queryFn: () => safetyApi.listLogs(locationId),
    enabled: !!locationId,
  });

  // This page renders outside AppLayout (so the printout has no app chrome),
  // which normally hydrates LocationContext — on a direct load/refresh, do it
  // here with the same query AppLayout uses.
  const { data: locationsData } = useQuery({
    queryKey: settingsKeys.locations(),
    queryFn: () => locationsApi.getAll(),
    enabled: !locationId,
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (locationId || !locationsData?.data?.length) return;
    const locs = locationsData.data.map((l) => ({
      id: l.id,
      name: l.name,
      address: l.address,
      city: l.city,
      state: l.state,
      phone: l.phone,
      isPrimary: l.isDefault,
    }));
    setLocations(locs);
    setCurrentLocation(locs.find((l) => l.isPrimary) ?? locs[0]);
  }, [locationId, locationsData, setCurrentLocation, setLocations]);

  if (!records) {
    return (
      <div className={styles.binder}>
        <Skeleton height={400} />
      </div>
    );
  }

  return (
    <div className={styles.binder}>
      <div className={styles.toolbar}>
        <button className={styles.backLink} onClick={() => navigate('/safety/records')}>
          <ArrowLeft size={13} aria-hidden /> Back to Records
        </button>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Printer size={14} aria-hidden />}
          onClick={() => window.print()}
        >
          Print / save as PDF
        </Button>
      </div>

      {/* Cover */}
      <header className={styles.cover}>
        <h1 className={styles.coverTitle}>Safety &amp; Training — Inspection Binder</h1>
        <p className={styles.coverMeta}>
          {currentLocation?.name ?? 'Practice location'} · generated {formatDate(new Date())}
        </p>
        <p className={styles.coverNote}>
          Contents: training completion records (with e-signatures), equipment &amp; operations
          log history, Exposure Control Plan, and the applicable-requirements table.
        </p>
      </header>

      {/* 1 — Completion records */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>1 · Training completion records</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Team member</th>
              <th>Course</th>
              <th>Topics</th>
              <th>Completed</th>
              <th>Score</th>
              <th>E-signature</th>
            </tr>
          </thead>
          <tbody>
            {records.completions.map((record) => (
              <tr key={record.id}>
                <td>
                  {record.staffName}
                  <span className={styles.sub}>{record.roleLabel}</span>
                </td>
                <td>
                  {record.courseTitle} v{record.courseVersion}
                </td>
                <td>{record.topics.join(', ')}</td>
                <td>{formatDate(record.completedAt)}</td>
                <td>{Math.round(record.quizScore * 100)}%</td>
                <td>
                  {record.signed
                    ? `${record.signerName} · ${
                        record.signedAt ? formatDate(record.signedAt) : ''
                      } · ${record.ackVersion}`
                    : 'Awaiting signature'}
                </td>
              </tr>
            ))}
            {records.completions.length === 0 && (
              <tr>
                <td colSpan={6}>No completion records on file.</td>
              </tr>
            )}
          </tbody>
        </table>
        <p className={styles.retention}>
          Retention: 3 years (OSHA training records) · 6 years (HIPAA-related records).
        </p>
      </section>

      {/* 2 — Equipment log history */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>2 · Equipment &amp; operations log history</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Equipment</th>
              <th>Result</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {(logs ?? []).map((entry) => (
              <tr key={entry.id}>
                <td>{formatDate(entry.loggedAt)}</td>
                <td>{entry.equipment.label ?? entry.equipment.type}</td>
                <td className={entry.result === 'fail' ? styles.fail : undefined}>
                  {entry.result.toUpperCase()}
                </td>
                <td>
                  {Object.entries(entry.values)
                    .map(([k, v]) => `${k}: ${String(v)}`)
                    .join(' · ')}
                  {entry.escalation ? ` — ${entry.escalation}` : ''}
                </td>
              </tr>
            ))}
            {(logs ?? []).length === 0 && (
              <tr>
                <td colSpan={4}>No log entries recorded.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {/* 3 — Exposure Control Plan */}
      {records.ecp && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            3 · Exposure Control Plan (version {records.ecp.version})
          </h2>
          <p className={styles.ecpMeta}>
            {records.ecp.reviewedAt
              ? `Last reviewed ${formatDate(records.ecp.reviewedAt)}`
              : 'Not yet reviewed'}
            {records.ecp.nextReview
              ? ` · next review due ${formatDate(records.ecp.nextReview)}`
              : ''}
          </p>
          {records.ecp.content.sections.map((section) => (
            <div key={section.heading} className={styles.ecpSection}>
              <h3 className={styles.ecpHeading}>{section.heading}</h3>
              <p className={styles.ecpBody}>{section.body}</p>
            </div>
          ))}
        </section>
      )}

      {/* 4 — Requirements table */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>4 · What applies — California</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Requirement</th>
              <th>Source</th>
              <th>Citation</th>
              <th>Handled as</th>
            </tr>
          </thead>
          <tbody>
            {records.caOverlay.map((row) => (
              <tr key={row.requirement}>
                <td>{row.requirement}</td>
                <td>{row.source}</td>
                <td>
                  {row.citation} · verified {formatDate(row.lastVerified)}
                </td>
                <td>
                  <PnTag action={row.pnAction} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <footer className={styles.attest}>{records.attestation}</footer>
    </div>
  );
}
